import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Input } from '@bruno/ui';
import type { HandoffFallbackStrategy, HandoffSettingsDTO } from '@bruno/shared';
import { getHandoffSettings, updateHandoffSettings } from '../api/team';
import { useAuth } from '../auth/AuthContext';

/**
 * Owner-only configuration of the per-org handoff broadcast window and
 * fallback behavior. Backed by `org_handoff_settings`. The defaults
 * (60s claim window, leave-a-message fallback, telegram fan-out on)
 * mirror `DEFAULT_HANDOFF_SETTINGS` on the API; if the row doesn't
 * exist yet the GET endpoint returns the same defaults.
 */
export function SettingsHandoffPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['team', 'handoff-settings'],
    queryFn: getHandoffSettings,
  });
  const [form, setForm] = useState<{
    claimWindowSeconds: number;
    fallbackStrategy: HandoffFallbackStrategy;
    notifyOnPending: boolean;
  } | null>(null);
  // Hydrate the form whenever the server payload changes (e.g. after a
  // successful save). We keep a separate piece of state instead of
  // editing the query cache so the user can revert via Cancel.
  useEffect(() => {
    if (data) {
      setForm({
        claimWindowSeconds: data.claimWindowSeconds,
        fallbackStrategy: data.fallbackStrategy,
        notifyOnPending: data.notifyOnPending,
      });
    }
  }, [data]);

  const save = useMutation({
    mutationFn: (body: Parameters<typeof updateHandoffSettings>[0]) =>
      updateHandoffSettings(body),
    onSuccess: (resp) => {
      qc.setQueryData(['team', 'handoff-settings'], resp);
    },
  });

  if (user?.role !== 'owner') {
    return (
      <div className="text-sm text-slate-500">
        Only the account owner can configure handoff behavior.
      </div>
    );
  }

  function dirty(): boolean {
    if (!data || !form) return false;
    return (
      form.claimWindowSeconds !== data.claimWindowSeconds ||
      form.fallbackStrategy !== data.fallbackStrategy ||
      form.notifyOnPending !== data.notifyOnPending
    );
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!form) return;
    save.mutate(form);
  }

  return (
    <div className="flex flex-col gap-4 max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold">Handoff settings</h1>
        <p className="text-sm text-slate-500">
          Control how visitor &ldquo;talk to a human&rdquo; requests are routed to your team.
        </p>
      </div>

      {isLoading || !form ? (
        <Card>
          <div className="text-sm text-slate-500">Loading…</div>
        </Card>
      ) : error ? (
        <Card>
          <div className="text-sm text-red-600">
            Failed to load settings: {(error as Error).message}
          </div>
        </Card>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Card>
            <div className="flex flex-col gap-3">
              <SectionHeader
                title="Claim window"
                description="When a visitor asks for a human, every available agent (in the dashboard and Telegram) sees a Take button. This is how long the offer stays available before falling back."
              />
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min={5}
                  max={600}
                  step={1}
                  value={String(form.claimWindowSeconds)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      claimWindowSeconds: clampInt(Number(e.target.value), 5, 600),
                    })
                  }
                  className="w-32"
                />
                <span className="text-sm text-slate-600">seconds (5 – 600)</span>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex flex-col gap-3">
              <SectionHeader
                title="Fallback when nobody claims"
                description="Action taken if the claim window lapses without any agent taking the conversation."
              />
              <div className="flex flex-col gap-2">
                <RadioRow
                  name="fallback"
                  value="offline_message"
                  current={form.fallbackStrategy}
                  onSelect={(v) => setForm({ ...form, fallbackStrategy: v })}
                  label="Show the leave-a-message form"
                  description="Visitor is invited to leave their email and a description; their ticket lands in the Inbox as an offline message."
                />
                <RadioRow
                  name="fallback"
                  value="auto_assign"
                  current={form.fallbackStrategy}
                  onSelect={(v) => setForm({ ...form, fallbackStrategy: v })}
                  label="Auto-assign to the least-busy agent"
                  description="If at least one agent is online, the conversation is force-assigned to them. If nobody is online, the leave-a-message form is shown anyway."
                />
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <SectionHeader
                  title="Telegram notifications"
                  description="Master switch for fanning out new pending handoffs to your Telegram bot. Per-agent linking is configured under the Telegram tab."
                />
              </div>
              <label className="flex items-center gap-2 mt-1">
                <input
                  type="checkbox"
                  checked={form.notifyOnPending}
                  onChange={(e) =>
                    setForm({ ...form, notifyOnPending: e.target.checked })
                  }
                />
                <span className="text-sm text-slate-700">
                  {form.notifyOnPending ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </div>
          </Card>

          <div className="flex items-center gap-3">
            <Button type="submit" loading={save.isPending} disabled={!dirty()}>
              Save changes
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={!dirty()}
              onClick={() =>
                setForm({
                  claimWindowSeconds: data!.claimWindowSeconds,
                  fallbackStrategy: data!.fallbackStrategy,
                  notifyOnPending: data!.notifyOnPending,
                })
              }
            >
              Revert
            </Button>
            {save.isError ? (
              <span className="text-sm text-red-600">
                {(save.error as Error).message}
              </span>
            ) : save.isSuccess && !dirty() ? (
              <Badge tone="green">Saved</Badge>
            ) : null}
          </div>
          <UpdatedHint settings={data ?? null} />
        </form>
      )}
    </div>
  );
}

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <div className="font-semibold text-slate-900">{title}</div>
      <div className="text-sm text-slate-500">{description}</div>
    </div>
  );
}

function RadioRow({
  name,
  value,
  current,
  onSelect,
  label,
  description,
}: {
  name: string;
  value: HandoffFallbackStrategy;
  current: HandoffFallbackStrategy;
  onSelect: (v: HandoffFallbackStrategy) => void;
  label: string;
  description: string;
}) {
  const id = `${name}-${value}`;
  return (
    <label
      htmlFor={id}
      className={`flex items-start gap-3 rounded-md border px-3 py-2 cursor-pointer ${
        current === value ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200'
      }`}
    >
      <input
        id={id}
        type="radio"
        name={name}
        value={value}
        checked={current === value}
        onChange={() => onSelect(value)}
        className="mt-1"
      />
      <div>
        <div className="text-sm font-medium text-slate-900">{label}</div>
        <div className="text-xs text-slate-500">{description}</div>
      </div>
    </label>
  );
}

function UpdatedHint({ settings }: { settings: HandoffSettingsDTO | null }) {
  if (!settings) return null;
  let when: string;
  try {
    when = new Date(settings.updatedAt).toLocaleString();
  } catch {
    when = settings.updatedAt;
  }
  return <div className="text-xs text-slate-400">Last updated {when}.</div>;
}

function clampInt(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min;
  const v = Math.round(n);
  if (v < min) return min;
  if (v > max) return max;
  return v;
}
